import type { ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";

import { Modal } from "./Modal";
import { IconButton } from "./IconButton";
import type { UiTheme } from "../theme";

// Bottom action-sheet chrome over the shared <Modal> scrim: a rounded-top card
// pinned to the bottom, slide-in, tap-out dismiss. Encodes the sheet shell
// (radius, bottom safe-area padding, optional title + ✕ close row) ONCE for the
// hand-rolled bottom sheets (EffortScaleModal / SupersetMenuSheet /
// TimelineHistorySheet, next/consolidation.md). The body is caller-composed.
//
// Safe-area inset is injected as `bottomInset` (px) — @jackedlogs/ui
// deliberately carries no react-native-safe-area-context dep, so the mount
// feeds useSafeAreaInsets().bottom (same seam as Maxes/EditMaxSheet). Web
// passes none.

type SheetBaseProps = {
  theme: UiTheme;
  visible: boolean;
  onRequestClose: () => void;
  /** Optional header title. */
  title?: string;
  /** Safe-area bottom inset (px) from the mount; 0 on web. */
  bottomInset?: number;
  children: ReactNode;
};

/**
 * The header ✕ close is all-or-nothing: passing `onClose` (which renders the
 * trailing ✕, usually === onRequestClose) REQUIRES an a11y label, so a
 * migration can never ship a mute close button. Omit both for sheets whose
 * dismiss is a full Cancel row in the body instead.
 */
type SheetCloseProps =
  | { onClose?: undefined; closeAccessibilityLabel?: undefined }
  | { onClose: () => void; closeAccessibilityLabel: string };

export type SheetProps = SheetBaseProps & SheetCloseProps;

export function Sheet({
  theme,
  visible,
  onRequestClose,
  title,
  onClose,
  closeAccessibilityLabel,
  bottomInset = 0,
  children,
}: SheetProps) {
  const styles = makeStyles(theme);
  return (
    <Modal
      theme={theme}
      visible={visible}
      onRequestClose={onRequestClose}
      animationType="slide"
      backdropStyle={styles.backdrop}
    >
      <View style={[styles.sheet, { paddingBottom: theme.spacing.s5 + bottomInset }]}>
        {title !== undefined || onClose ? (
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={1}>
              {title ?? ""}
            </Text>
            {onClose ? (
              // Shared IconButton (36px box + hitSlop → ≥44 touch, required
              // a11y label) instead of a hand-sized ✕ glyph.
              <IconButton
                theme={theme}
                name="close"
                accessibilityLabel={closeAccessibilityLabel ?? ""}
                onPress={onClose}
              />
            ) : null}
          </View>
        ) : null}
        {children}
      </View>
    </Modal>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    backdrop: { justifyContent: "flex-end" },
    sheet: {
      backgroundColor: p.bg,
      borderTopLeftRadius: r.r4,
      borderTopRightRadius: r.r4,
      paddingHorizontal: s.s4,
      paddingTop: s.s4,
      gap: s.s3,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    title: { color: p.ink, fontSize: t.sizes.lg, fontWeight: "700", flexShrink: 1 },
  });
}
